// selected-trace.js

import escape from "lodash/escape";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import queryString from "query-string";
import uniq from "lodash/uniq";
import values from "lodash/values";
import { createSelector } from "reselect";
import { put, call, takeLatest } from "redux-saga/effects";

import { encodeUrl, getWithStatusCheck } from "../helpers/fetch.js";
import { getParsedQuery, getOriginSpanId } from "apm/helpers/router";
import { idFor } from "utils/id";
import { mergeRecordById } from "../helpers/reducers";
import {
  formatMicrosecondsForDisplay,
  formatPercentageForDisplay,
} from "../helpers/formatters.js";

export const ASYNC = "(async)";
export const SELECTION_TYPE_ERROR = "errors";
export const SELECTION_TYPE_QUERY = "queries";
export const SELECTION_TYPE_LAYER = "layer";
export const SELECTION_TYPE_SERVICE = "service";
export const SELECTION_INCLUDE_ALL = "all";
export const SELECTION_INCLUDE_ASYNC = "async";
export const SELECTION_INCLUDE_SYNC = undefined;

export const INITIAL_STATE = {
  isLoading: false,
  isProfileLoading: false,
  events: [],
  extents: {
    tree: {},
    byId: {},
  },
  profile: {},
  fov: {
    start: 0,
    end: 100,
  },
};

// action types
const FETCH_EVENTS = "trace-web/selected-trace/FETCH_EVENTS";
const FETCH_EVENTS_SUCCESS = "trace-web/selected-trace/FETCH_EVENTS_SUCCESS";
const FETCH_EVENTS_FAILURE = "trace-web/selected-trace/FETCH_EVENTS_FAILURE";

const FETCH_EXTENTS = "trace-web/selected-trace/FETCH_EXTENTS";
const FETCH_EXTENTS_SUCCESS = "trace-web/selected-trace/FETCH_EXTENTS_SUCCESS";

const FETCH_EXTENT = "trace-web/selected-trace/FETCH_EXTENT";
const FETCH_EXTENT_SUCCESS = "trace-web/selected-trace/FETCH_EXTENT_SUCCESS";

const FETCH_PROFILE = "trace-web/selected-trace/FETCH_PROFILE";
const FETCH_PROFILE_SUCCESS = "trace-web/selected-trace/FETCH_PROFILE_SUCCESS";
const FETCH_PROFILE_FAILURE = "trace-web/selected-trace/FETCH_PROFILE_FAILURE";

const SET_TRACE_FOV = "trace-web/selected-trace/SET_TRACE_FOV";

const RESET_TRACE_STATE = "trace-web/selected-trace/RESET_TRACE_STATE";

// reducer
const selectedTraceReducer = (state = INITIAL_STATE, action) => {
  let byId;
  switch (action.type) {
    case FETCH_EXTENTS:
      return {
        ...state,
        isLoading: true,
      };
    case FETCH_EVENTS_SUCCESS:
      return {
        ...state,
        id: action.traceId,
        events: action.traceEvents,
      };
    case FETCH_EXTENTS_SUCCESS:
      byId = mergeRecordById(action.firstExtent, state.extents.byId);
      return {
        ...state,
        isLoading: false,
        extents: {
          tree: action.tree,
          byId: byId,
        },
      };
    case FETCH_EXTENT_SUCCESS:
      byId = mergeRecordById(action.extent, state.extents.byId);
      return {
        ...state,
        extents: {
          tree: state.extents.tree,
          byId: byId,
        },
      };
    case FETCH_PROFILE:
      return {
        ...state,
        isProfileLoading: true,
      };
    case FETCH_PROFILE_SUCCESS:
      return {
        ...state,
        isProfileLoading: false,
        profile: action.profile,
      };
    case FETCH_PROFILE_FAILURE:
      return {
        ...state,
        isProfileLoading: false,
        profile: {
          ...state.profile,
          error: action.ex,
        },
      };
    case RESET_TRACE_STATE:
      return {
        ...INITIAL_STATE,
      };
    case SET_TRACE_FOV:
      return {
        ...state,
        fov: {
          start: action.start,
          end: action.end,
        },
      };
    default:
      return state;
  }
};
export default selectedTraceReducer;

// action creators
export const fetchTraceEvents = (traceId, orgId) => {
  return {
    type: FETCH_EVENTS,
    traceId,
    orgId,
  };
};

export const fetchSuccess = (traceEvents, traceId) => {
  return {
    type: FETCH_EVENTS_SUCCESS,
    traceId,
    traceEvents,
  };
};

export const fetchFailure = ex => {
  return {
    type: FETCH_EVENTS_FAILURE,
    ex,
  };
};

export const fetchTraceExtents = (traceId, orgId) => {
  return {
    type: FETCH_EXTENTS,
    traceId,
    orgId,
  };
};

export const fetchTraceExtentsSuccess = (extents, firstExtent) => {
  return {
    type: FETCH_EXTENTS_SUCCESS,
    tree: extents.extents,
    firstExtent: firstExtent.extent,
  };
};

export const fetchTraceExtent = (traceId, extentId) => {
  return {
    type: FETCH_EXTENT,
    traceId,
    extentId,
  };
};

export const fetchTraceExtentSuccess = (extent, extentId) => {
  return {
    type: FETCH_EXTENT_SUCCESS,
    extent,
    extentId,
  };
};

export const fetchTraceProfile = (traceId, spanId, startTime, endTime) => {
  return {
    type: FETCH_PROFILE,
    traceId,
    spanId,
    startTime,
    endTime,
  };
};

export const fetchTraceProfileSuccess = (profile, hasProfile) => {
  return {
    type: FETCH_PROFILE_SUCCESS,
    profile,
    hasProfile,
  };
};

export const fetchTraceProfileFailure = ex => {
  return {
    type: FETCH_PROFILE_FAILURE,
    ex,
  };
};

export const resetTraceState = () => {
  return {
    type: RESET_TRACE_STATE,
  };
};

export const setTraceFOV = (start, end) => {
  return {
    type: SET_TRACE_FOV,
    start,
    end,
  };
};

// saga
export function* selectedTraceWatcher() {
  yield [
    selectedTraceEventsWatcher(),
    selectedTraceExtentWatcher(),
    selectedTraceExtentsWatcher(),
    selectedTraceProfileWatcher(),
  ];
}

export function* selectedTraceEventsWatcher() {
  yield takeLatest(FETCH_EVENTS, selectedTraceEventsWorker);
}

export function* selectedTraceEventsWorker(action) {
  try {
    const url = `/v1/apm/traces/${action.traceId}/events`;
    const json = yield call(getWithStatusCheck, url);
    yield put(fetchSuccess(json));
  } catch (err) {
    yield put(fetchFailure(err));
  }
}

export function* selectedTraceExtentsWatcher() {
  yield takeLatest(FETCH_EXTENTS, selectedTraceExtentsWorker);
}

export function* selectedTraceExtentsWorker(action) {
  try {
    let url = `/v1/apm/traces/${action.traceId}/extents`;
    const tree = yield call(getWithStatusCheck, url);
    url = `/v1/apm/traces/${action.traceId}/extents/0`;
    const firstExtent = yield call(getWithStatusCheck, url);
    yield put(fetchTraceExtentsSuccess(tree, firstExtent));
  } catch (err) {
    yield put(fetchFailure(err));
  }
}

export function* selectedTraceExtentWatcher() {
  yield takeLatest(FETCH_EXTENT, selectedTraceExtentWorker);
}

export function* selectedTraceExtentWorker(action) {
  try {
    const url = `/v1/apm/traces/${action.traceId}/extents/${action.extentId}`;
    const result = yield call(getWithStatusCheck, url);
    const extent = result.extent;
    yield put(fetchTraceExtentSuccess(extent, extent.id));
  } catch (err) {
    yield put(fetchFailure(err));
  }
}

export function* selectedTraceProfileWatcher() {
  yield takeLatest(FETCH_PROFILE, selectedTraceProfileWorker);
}

export function* selectedTraceProfileWorker(action) {
  try {
    let params = {
      start_time: action.startTime,
      end_time: action.endTime,
    };
    const url = encodeUrl(
      `/v1/apm/traces/${action.traceId}/profile/${action.spanId}`,
      params
    );
    const result = yield call(getWithStatusCheck, url);
    yield put(fetchTraceProfileSuccess(result.profile, result.hasProfile));
  } catch (err) {
    yield put(fetchTraceProfileFailure(err));
  }
}

// selectors - extent-based
const getExtentsTree = state => state.entities.selectedTrace.extents.tree;
const getExtentsById = state => state.entities.selectedTrace.extents.byId;
export const getIsLoading = state => state.entities.selectedTrace.isLoading;
export const getStartTime = state => {
  const start = get(state.entities.selectedTrace.extents.tree, "root.start"); // microseconds
  return start / 1000 / 1000; // to seconds
};
export const getDuration = state => {
  const start = get(state.entities.selectedTrace.extents.tree, "root.start"); // microseconds
  const end = get(state.entities.selectedTrace.extents.tree, "root.end"); // microseconds
  return formatMicrosecondsForDisplay(end - start).formattedValue;
};
export const getHTTP = state =>
  get(state.entities.selectedTrace.extents.byId, "0.HTTP") || {};
export const getTraceSpanSort = state =>
  get(state, "ui.sorts.traceSpans.sort") || "duration";
export const getTraceSpanDirection = state => {
  const direction = get(state, "ui.sorts.traceSpans.direction") || "DESC";
  return direction.toLowerCase();
};
export const getSpanMultiSelectSort = state =>
  get(state, "ui.sorts.spanMultiSelect.sort") || "duration";
export const getSpanMultiSelectDirection = state => {
  const direction = get(state, "ui.sorts.spanMultiSelect.direction") || "DESC";
  return direction.toLowerCase();
};
export const getTraceErrorsSort = state =>
  get(state, "ui.sorts.traceErrors.sort") || "time";
export const getTraceErrorsDirection = state => {
  const direction = get(state, "ui.sorts.traceErrors.direction") || "ASC";
  return direction.toLowerCase();
};
export const getSelectedExtentProfile = state =>
  state.entities.selectedTrace.profile;
export const getSelectedExtentProfileError = state =>
  state.entities.selectedTrace.profile.error;
export const getIsProfileLoading = state =>
  state.entities.selectedTrace.isProfileLoading;

const markAsyncByDescendingTree = (node, isAsync) => {
  const newIsAsync = isAsync || node.async;
  const newNode = {
    ...node,
    isAsync: newIsAsync,
  };
  if (node.children) {
    newNode.children = node.children.map(child => {
      return markAsyncByDescendingTree(child, newIsAsync);
    });
  }
  return newNode;
};

const getExtentsTreeWithAsyncMarked = createSelector(
  [getExtentsTree],
  extentTree => {
    if (!extentTree.root) {
      return extentTree;
    }
    return {
      root: markAsyncByDescendingTree(extentTree.root, false),
    };
  }
);

export const getExtentsCount = createSelector([getExtentsTree], extentsTree => {
  let count = 0;

  traverse(extentsTree.root, node => {
    count++;
  });

  return count;
});

export const getSelectedTraceId = (_, props) => {
  let traceId = props.match.params.traceId;

  /* support full x-trace ID if copy-pasted to URL
     per https://docs.google.com/document/d/13EhOe8faw8zXLhTwPUUJc-HxyV3M94hcV5TD0skCGSk/edit# */
  if (traceId.length > 40) {
    return traceId.substring(2, 42);
  } else {
    return traceId;
  }
};

export const getSelectedExtentId = (state, props) => {
  const spanId = props.match.params.spanId;
  if (!spanId) {
    return;
  }
  const tree = getExtentsTree(state, props) || {};
  let lookup = {};

  traverse(tree.root, node => {
    if (node.span_id === spanId || node.id.toString() === spanId) {
      lookup = node;
    }
  });

  return lookup.id;
};

export const getSelectionType = (state, props) => {
  return getParsedQuery(state, props).selectionType;
};

export const getSelection = (state, props) => {
  return getParsedQuery(state, props).selection;
};

export const getSelectionInclude = (state, props) => {
  return getParsedQuery(state, props).selectionInclude;
};

export const getSelectedExtent = createSelector(
  [getSelectedExtentId, getExtentsById],
  (extentId, extentsById) => {
    return extentsById[extentId];
  }
);

export const getSelectedExtentEvents = createSelector(
  [getSelectedExtent],
  selectedExtent => {
    return (selectedExtent && selectedExtent.Events) || [];
  }
);

export const getSelectedExtentErrors = createSelector(
  [getSelectedExtent],
  selectedExtent => {
    let errors = [];
    if (selectedExtent && selectedExtent.Errors) {
      selectedExtent.Errors.forEach(error => {
        errors.push({
          time: error.Time,
          type: error.Class,
          message: error.Message,
          backtrace: error.Backtrace,
          status: get(selectedExtent, "HTTP.Status"),
          host: selectedExtent.Host,
        });
      });
    }
    return errors;
  }
);

export const getSelectedExtentErrorsCount = createSelector(
  [getSelectedExtentErrors],
  errors => errors.length
);

export const getSelectedExtentBacktraces = createSelector(
  [getSelectedExtent],
  selectedExtent => {
    return (selectedExtent && selectedExtent.Backtraces) || [];
  }
);

export const hasBacktraces = createSelector(
  [getSelectedExtentBacktraces],
  backtraces => backtraces.length > 0
);

export const getSelectedExtentQuery = createSelector(
  [getSelectedExtent],
  selectedExtent => {
    return selectedExtent && selectedExtent.Query;
  }
);

const getSelectedExtentEntryEvent = createSelector(
  [getSelectedExtentEvents],
  events => events.find(event => event.Label == "entry")
);

export const getSelectedExtentStartTime = createSelector(
  [getSelectedExtentEntryEvent],
  entry => entry && entry.Timestamp
);

export const getSelectedExtentSpanId = createSelector(
  [getSelectedExtentEntryEvent],
  entry => entry && entry.op_id
);

export const getSelectedExtentEndTime = createSelector(
  [getSelectedExtentEvents],
  events => {
    const exit = events.find(event => event.Label == "exit");
    return exit && exit.Timestamp;
  }
);

export const getTraceUri = createSelector([getHTTP], http => {
  const { Host, URL, QueryString } = http;
  return {
    path: escape(Host) + escape(URL),
    query: queryString.parse(QueryString),
  };
});

export const getTraceController = createSelector(
  [getHTTP],
  http => http && http.Controller
);

export const getTraceAction = createSelector(
  [getHTTP],
  http => http && http.Action
);

/**
 * Simplified version from legacy:
 *
 *   https://github.com/librato/tracelons/blob/4a547cd894735532e808036c1199dda7ef600c56/tracelytics/tracelytics/public/lib/trace-details/TraceDataModel.js#L108-L134
 */

export const getDescriptors = state => {
  const traceTree = state.entities.selectedTrace.extents.tree;
  const servicesTable = [];
  traceTree.descriptors.map(item => {
    servicesTable.push(item.app);
  });
  let descriptors = traceTree.descriptors;

  traverse(traceTree.root, node => {
    if (servicesTable.indexOf(node.service) == -1) {
      descriptors.push({
        app: node.service,
        host: node.host,
        layer: node.layer,
      });
      servicesTable.push(node.service);
    }
  });
  return descriptors;
};

export const getTraceServices = createSelector(
  [getDescriptors],
  descriptors => {
    if (!descriptors) {
      return [];
    }
    return uniq(
      descriptors.map(d => {
        return d.app;
      })
    );
  }
);

const traverse = (root, callback) => {
  const recurse = currentNode => {
    if (!currentNode) return;

    if (currentNode.children) {
      currentNode.children.forEach(child => {
        recurse(child);
      });
    }

    callback(currentNode);
  };

  recurse(root);
};

const getTotalDuration = node => {
  return node.end - node.start;
};

const addOrUpdateQueries = (obj, node) => {
  const fp = node.query_fp;
  const total = getTotalDuration(node);
  const { id, layer, isAsync } = node;

  if (fp in obj) {
    obj[fp].count++;
    obj[fp].total += total;
    obj[fp].average = obj[fp].total / obj[fp].count;
  } else {
    const count = 1;
    const average = total / count;
    obj[fp] = {
      id,
      isAsync,
      span: layer,
      query: fp,
      count,
      total,
      average,
    };
  }
};

export const getTraceQueries = createSelector(
  [getExtentsTreeWithAsyncMarked],
  extentTree => {
    const lookup = {};

    traverse(extentTree.root, node => {
      if (node.query_fp) {
        addOrUpdateQueries(lookup, node);
      }
    });

    return values(lookup);
  }
);

export const getTraceLayers = createSelector([getDescriptors], descriptors => {
  if (!descriptors) {
    return [];
  }
  return uniq(descriptors.map(d => d.layer));
});

const spanLayerKey = node => {
  return node.isAsync ? node.layer : `${node.layer}//a`;
};

export const getSelfDuration = node => {
  let duration = getTotalDuration(node);

  if (node.children) {
    node.children.forEach(child => {
      if (!child.async) {
        const childDuration = getTotalDuration(child);
        duration -= childDuration;
      }
    });
  }

  return duration;
};

export const getSpanDuration = (obj, node) => {
  const oKey = spanLayerKey(node);
  const n = Object.assign({}, node);
  let duration = getTotalDuration(node);

  if (!(oKey in obj) || node.start >= obj[oKey].end) {
    return duration;
  }

  for (let interval of obj[oKey].intervals) {
    const { start, end } = interval;
    const isStartTimeInside = n.start >= start && n.start < end;
    const isEndTimeInside = n.end > start && n.end <= end;

    // Logical OR - if periods overlap, return only one of them
    if (isStartTimeInside && isEndTimeInside) {
      // Both (start, end) inside
      // |----------------|
      //      |-------|
      duration = 0;
      break;
    } else if (!isStartTimeInside && isEndTimeInside) {
      // Started earlier
      //    |---------|
      // |--------|
      duration = duration - (n.end - start);
      n.end = start;
    } else if (isStartTimeInside && !isEndTimeInside) {
      // Started during the previous span, ended after
      // |---------|
      //      |----------|
      duration = duration - (end - n.start);
      n.start = end;
    } else if (start > n.start && end < n.end) {
      // Reverse order
      //     |---|
      // |-------------|
      duration = duration - (end - start);
    }
  }

  return Math.max(duration, 0);
};

export const getMultipleSpanLayersDuration = spans => {
  const lookup = {};
  let duration = 0;

  spans.forEach(span => {
    const oKey = spanLayerKey(span);

    duration += getSpanDuration(lookup, span);

    lookup[oKey] = lookup[oKey] || {};
    lookup[oKey].end = span.end;
    lookup[oKey].start = span.start;
    lookup[oKey].intervals = [
      ...(lookup[oKey].intervals || []),
      { end: span.end, start: span.start },
    ];
  });

  return duration;
};

export const addOrUpdateSpanLayer = (obj, node, traceDuration) => {
  const oKey = spanLayerKey(node);
  let duration = getSpanDuration(obj, node);
  let errors = 0;

  if (node.errors) {
    errors += node.errors.length;
  }

  if (oKey in obj) {
    // update
    obj[oKey].isAsync = obj[oKey].isAsync || node.isAsync;
    obj[oKey].count++;
    obj[oKey].end = Math.max(obj[oKey].end, node.end);
    obj[oKey].start = Math.min(obj[oKey].start, node.start);
    (obj[oKey].intervals = [
      ...obj[oKey].intervals,
      { end: node.end, start: node.start },
    ]),
      (obj[oKey].duration += duration);
    obj[oKey].durationPercent = node.isAsync
      ? null
      : obj[oKey].duration / traceDuration * 100;
    obj[oKey].errors += errors;
  } else {
    // add
    const count = 1;
    const durationPercent = node.isAsync
      ? null
      : duration / traceDuration * 100;
    obj[oKey] = {
      isAsync: node.isAsync,
      span: node.layer,
      intervals: [{ end: node.end, start: node.start }],
      end: node.end,
      start: node.start,
      duration,
      durationPercent,
      count,
      errors,
    };
  }
};

const getTraceSpanLayers = createSelector(
  [getExtentsTreeWithAsyncMarked],
  extentTree => {
    if (!extentTree.root) {
      return [];
    }

    const lookup = {};
    const traceDuration = getTotalDuration(extentTree.root);

    traverse(extentTree.root, node => {
      addOrUpdateSpanLayer(lookup, node, traceDuration);
    });

    return lookup;
  }
);

const getTraceTotalDuration = createSelector([getExtentsTree], extentTree => {
  if (extentTree.root) {
    return getTotalDuration(extentTree.root);
  }
});

export const getSpanMultiSelectBySelection = createSelector(
  [
    getSelectionType,
    getSelection,
    getSelectionInclude,
    getExtentsTreeWithAsyncMarked,
    getSpanMultiSelectSort,
    getSpanMultiSelectDirection,
  ],
  (selectionType, selection, selectionInclude, extentTree, sort, direction) => {
    let spanLayers = [];

    if (!extentTree.root || !selectionType || !getSelection) {
      return spanLayers;
    }

    const totalDuration = getTotalDuration(extentTree.root);

    traverse(extentTree.root, node => {
      let service;
      let hasSelection,
        hasSelectionInclude = false;
      switch (selectionInclude) {
        case SELECTION_INCLUDE_SYNC: {
          hasSelectionInclude = node.isAsync == SELECTION_INCLUDE_SYNC;
          break;
        }
        case SELECTION_INCLUDE_ASYNC: {
          hasSelectionInclude = node.isAsync == true;
          break;
        }
        case SELECTION_INCLUDE_ALL: {
          hasSelectionInclude = true;
          break;
        }
      }

      if (hasSelectionInclude) {
        switch (selectionType) {
          case SELECTION_TYPE_LAYER: {
            hasSelection = node.layer == selection;
            break;
          }
          case SELECTION_TYPE_QUERY: {
            hasSelection = node.query_fp && idFor(node.query_fp) == selection;
            break;
          }
          case SELECTION_TYPE_ERROR: {
            if (node.errors) {
              const suitableError = node.errors.find(
                error => idFor(error.Message) === selection
              );
              if (suitableError) {
                hasSelection = true;
              }
            }
            break;
          }
          case SELECTION_TYPE_SERVICE: {
            hasSelection = node.service == selection;
            break;
          }
        }
        if (hasSelection) {
          const duration = getTotalDuration(node);
          const selfDuration = getSelfDuration(node);
          const durationPercent = node.isAsync
            ? ASYNC
            : duration / totalDuration * 100;
          const spanLayer = Object.assign(
            {
              duration: duration,
              durationPercent: durationPercent,
              selfDuration: selfDuration,
              service,
            },
            node
          );

          spanLayers.push(spanLayer);
        }
      }
    });

    return values(orderBy(spanLayers, sort, direction));
  }
);

export const getTraceSpanLayerBreakdown = createSelector(
  [getTraceSpanLayers, getTraceSpanSort, getTraceSpanDirection],
  (spanLayers, sort, direction) => {
    return values(orderBy(spanLayers, sort, direction));
  }
);

export const getSpanMultiSelectBreakdownBySelection = createSelector(
  [
    getSpanMultiSelectBySelection,
    getTraceTotalDuration,
    getSelectionType,
    getSelection,
    getSelectionInclude,
  ],
  (spans, totalDuration, selectionType, selection, selectionInclude) => {
    const hasSpans = spans.length > 0;
    if (!hasSpans) return;

    const duration = getMultipleSpanLayersDuration(spans);
    const isAsync = spans.some(span => span.isAsync);

    const summary = {
      isAsync,
      count: spans.length,
      selection,
      duration: formatMicrosecondsForDisplay(duration).formattedValue,
      durationPercent: isAsync
        ? { value: ASYNC, units: "" }
        : formatPercentageForDisplay(duration / totalDuration * 100)
            .formattedValue,
    };

    switch (selectionType) {
      case SELECTION_TYPE_LAYER: {
        summary.span = selection;
        break;
      }
      case SELECTION_TYPE_QUERY: {
        // query_fp is identical for all spans
        summary.query = spans[0].query_fp;
        break;
      }
      case SELECTION_TYPE_ERROR: {
        if (spans[0].errors) {
          const suitableError = spans[0].errors.find(
            error => idFor(error.Message) === selection
          );
          if (suitableError) {
            summary.error = suitableError.Message;
          } else {
            summary.error = "";
          }
        }
        break;
      }
      case SELECTION_TYPE_SERVICE: {
        summary.service = selection;
        break;
      }
    }

    return summary;
  }
);

const searchTreeForId = (node, id) => {
  let queue = [node];
  while (queue.length > 0) {
    const next = queue.shift();
    if (next.id === id) {
      return next;
    }
    if (next.children) {
      queue.push(...next.children);
    }
  }
};

export const getSelectedExtentKpis = createSelector(
  [getSelectedExtentId, getExtentsTreeWithAsyncMarked, getTraceSpanLayers],
  (selectedExtentId, extentsTree, spanLayers) => {
    if (isNaN(selectedExtentId)) {
      return {};
    }
    if (!extentsTree || !extentsTree.root) {
      return {};
    }

    const extent = searchTreeForId(extentsTree.root, +selectedExtentId);
    if (!extent) {
      return {};
    }

    const oKey = spanLayerKey(extent);
    const span = spanLayers[oKey];
    if (!span) {
      return {};
    }

    const traceDuration = getTotalDuration(extentsTree.root);
    const duration = getTotalDuration(extent);
    const selfDuration = getSelfDuration(extent);

    const asyncValue = { value: ASYNC, units: "" };
    const durationPercent = span.isAsync
      ? asyncValue
      : formatPercentageForDisplay(duration / traceDuration * 100)
          .formattedValue;
    const selfDurationPercent = span.isAsync
      ? asyncValue
      : formatPercentageForDisplay(selfDuration / traceDuration * 100)
          .formattedValue;

    return {
      duration: formatMicrosecondsForDisplay(duration).formattedValue,
      selfDuration: formatMicrosecondsForDisplay(selfDuration).formattedValue,
      durationPercent,
      selfDurationPercent,
    };
  }
);

export const getSelectedExtentService = createSelector(
  [getSelectedExtent],
  selectedExtent => {
    return selectedExtent && selectedExtent.App;
  }
);

export const getTraceErrorsBreakdown = createSelector(
  [getExtentsTreeWithAsyncMarked, getTraceErrorsSort, getTraceErrorsDirection],
  (extentsTree, sort, direction) => {
    if (!extentsTree.root) {
      return [];
    }

    const errors = [];

    traverse(extentsTree.root, node => {
      if (node.errors) {
        node.errors.forEach(error => {
          errors.push({
            id: node.id,
            span: node.layer,
            error: error.Message,
            host: node.host,
            time: error.Time / 1000, // convert microseconds to milliseconds
            isAsync: node.isAsync,
          });
        });
      }
    });

    return orderBy(errors, sort, direction);
  }
);

export const getErrorCount = createSelector(
  [getTraceErrorsBreakdown],
  errors => errors.length
);

export const getRootExtentEvents = createSelector(
  [getExtentsTree, getExtentsById],
  (extentsTree, extentsById) => {
    const rootId = extentsTree && extentsTree.root && extentsTree.root.id;
    const extent = extentsById[rootId];
    return extent ? extent.Events : [];
  }
);

export const getProfileSpans = createSelector([getRootExtentEvents], events => {
  const event = events.find(e => e.ProfileSpans);
  return event ? event.ProfileSpans : undefined;
});

const getIsFunctionProfileAvailable = createSelector(
  [getSelectedExtentProfile, getSelectedExtentProfileError],
  (profile, profileError) => !isEmpty(profile) && !profileError
);

export const isFunctionProfileVisible = createSelector(
  [getProfileSpans, getIsFunctionProfileAvailable],
  (profileSpans, isFunctionProfileAvailable) =>
    isFunctionProfileAvailable || profileSpans !== undefined
);

export const isFunctionProfileUpgradeVisible = createSelector(
  [getProfileSpans, getIsFunctionProfileAvailable],
  (profileSpans, isFunctionProfileAvailable) =>
    !isFunctionProfileAvailable && profileSpans === -1
);

export const isFunctionProfileMissingInformation = createSelector(
  [
    getProfileSpans,
    getIsFunctionProfileAvailable,
    getSelectedExtentProfileError,
  ],
  (profileSpans, isFunctionProfileAvailable, profileError) =>
    !isFunctionProfileAvailable && (profileSpans === 0 || profileError)
);

// selectors - event-based
const getEvents = state => state.entities.selectedTrace.events;

// returns events with integer Timestamp_u, sorted by time
export const getTraceEvents = createSelector([getEvents], events => {
  return events
    .map(event => {
      event.fields.Timestamp_u = parseInt(event.fields.Timestamp_u, 10);
      return event;
    })
    .sort((a, b) => {
      return a.fields.Timestamp_u - b.fields.Timestamp_u;
    });
});

// returns a map of [Layer][Label] => count, including Profiling spans as a special Layer
export const getTraceEventsLayerMap = createSelector(
  [getTraceEvents],
  events => {
    let layerMap = {};
    events.forEach(event => {
      let type = event.fields["Label"] ? event.fields["Label"] : "unknown";

      let layer = "unknown";
      if (event.fields["Spec"] == "profiling") {
        layer = "profiling";
      } else {
        layer = event.fields["Layer"] ? event.fields["Layer"] : "unknown";
      }
      if (!(layer in layerMap)) {
        layerMap[layer] = {};
      }

      layerMap[layer][type] = layerMap[layer][type]
        ? layerMap[layer][type] + 1
        : 1;
    });
    return layerMap;
  }
);

export const getRootSpanService = createSelector([getExtentsTree], tree =>
  get(tree, "root.service")
);

export const getOriginServiceName = (state, props) =>
  props.match.params.service;

export const getOriginOrSelectedSpanId = createSelector(
  [getSelectedExtentId, getOriginSpanId],
  (selectedSpanId, originSpanId) => {
    return originSpanId || selectedSpanId;
  }
);

export const getOriginServiceRoot = createSelector(
  [
    getExtentsTreeWithAsyncMarked,
    getOriginServiceName,
    getOriginOrSelectedSpanId,
  ],
  (tree, service, id) => {
    const root = get(tree, "root");

    if (!root || !service) {
      return;
    }

    if (id === undefined) {
      return {};
    }

    return serviceRootLookup(root, service, parseInt(id));
  }
);

const serviceRootLookup = (node, service, id) => {
  if (node.id === id) {
    return node;
  }

  if (!node.children || node.children.length === 0) {
    return;
  }

  let lookup;

  node.children.some(child => {
    const nodeFound = serviceRootLookup(child, service, id);
    if (nodeFound) {
      lookup = node.service === service ? node : nodeFound;
      return true;
    }
  });

  return lookup;
};
